import axios, { AxiosRequestConfig, AxiosRequestHeaders } from "axios";
import { MemberEligibilityInfo } from "../types";

interface RequestConfig<RequestBodyType> {
  readonly endpoint: string;
  readonly api?: "api" | "authnz";
  readonly body?: RequestBodyType;
  readonly useAuth?: boolean;
  readonly removeReferrerUrlHeader?: boolean;
}

export interface ViewRequest {
  readonly view_external_member_id: string;
  readonly view_person_code: string;
  readonly view_client_id: string;
}

export function getViewRequest(eligibility: MemberEligibilityInfo): ViewRequest {
  return {
    view_external_member_id: eligibility.member.external_member_id,
    view_client_id: eligibility.plan.client_id,
    view_person_code: eligibility.member.person_code,
  };
}

export function getEligibilityBasedQueryKey(eligibility: MemberEligibilityInfo): string[] {
  return [eligibility.plan.client_id, eligibility.member.external_member_id, eligibility.member.person_code];
}

export const request = async <RequestBodyType, ResponseType>(conf: RequestConfig<RequestBodyType>, authToken?: string): Promise<ResponseType> => {
  const apiToUse: typeof conf.api = conf.api === "authnz" ? "authnz" : "api";

  // by default, send an auth token
  let shouldUseAuth = true;
  if (conf.useAuth === false) {
    shouldUseAuth = false;
  } else if (conf.api === "authnz") {
    shouldUseAuth = false;
  }

  const headers = {} as AxiosRequestHeaders;

  if (shouldUseAuth && authToken) {
    headers["Authorization"] = `Bearer ${authToken}`;
  } else if (shouldUseAuth) {
    throw new Error("No auth token supplied for request!");
  }

  let axiosBaseUrl = site.baseUrl;
  if (apiToUse === "authnz") {
    axiosBaseUrl = site.authUrl;
  }

  const client = axios.create({
    baseURL: axiosBaseUrl,
    headers: {
      "X-Referrer-Url": window.location.href,
    },
  });

  if (conf.removeReferrerUrlHeader) {
    delete client.defaults.headers["X-Referrer-Url"];
  }

  const options: AxiosRequestConfig = {
    url: conf.endpoint,
    method: "POST",
    baseURL: axiosBaseUrl,
    headers: headers,
    data: conf.body,
  };

  try {
    const response = await client(options);
    return response.data;
  } catch (error) {
    let errorResult = {};

    // normalize the error response
    if (error.response) {
      // Request was made but server responded with a 500
      if (error.response.status === 500) {
        errorResult = {
          message: "Network Error",
        };
      } else {
        errorResult = error.response;
      }
    } else if (error.message) {
      errorResult = {
        message: error.message,
      };
    } else if (typeof error === "string") {
      errorResult = {
        message: error,
      };
    } else {
      console.error(error);
    }

    throw errorResult;
  }
};
